import {
  requestSignatoryIsEditable,
  requestUpdateSignatory,
} from "@/state/signatory/SignatoryEffects";
import { useTransactionSettings } from "@/state/transactions/TransactionSettingsHook";
import { ActiveUser } from "@/type/core/auth/auth.types";
import { Right } from "@/type/core/offices/offices.types";
import { sortSignatories } from "@/utils/helpers/signatories-helper";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import InfoModal from "@components/core/modals/InfoModal";
import SortableTable from "@components/core/tables/SortableTable";
import {
  requestGetTransactionDetail,
  requestPutReorderSignatories,
} from "@state/transactions/TransactionEffects";
import ChevronIcon from "@static/core/icons/icon-chevron.svg?react";
import ReloadIcon from "@static/core/icons/icon-reload.svg?react";
import { Document, Signatory } from "@type/core/dashboard/dashboard.types";
import { TransactionDetail } from "@type/core/transaction/transaction.types";
import { CANCELED_CAUSE } from "@utils/Constant";
import { columns } from "@views/core/extranet/transactions/details/columns/TransactionSignatoriesColumns";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RegexHelper } from "@utils/helpers/regex-helper";
import BasicButton from "@components/core/buttons/BasicButton";

interface TransactionSignatoriesSectionProps {
  user?: ActiveUser;
  officeRights: Right[];
  transactionId?: string;
  transaction: TransactionDetail;
  signatories: Signatory[];
  documents: Document[];
  authorized: boolean;
  signatureDegradedMode: boolean;
  className?: string;
}

const TransactionSignatoriesSection: FunctionComponent<
  TransactionSignatoriesSectionProps
> = ({
  user,
  officeRights,
  transactionId,
  transaction,
  signatories,
  documents,
  authorized,
  signatureDegradedMode,
  className = "",
}) => {
  const { t } = useTranslation();

  const transactionSettings = useTransactionSettings();
  const [initialSignatories, setInitialSignatories] = useState<Signatory[]>([]);
  const [updatedSignatories, setUpdatedSignatories] =
    useState<Signatory[]>(signatories);
  const [temporaryEditableSignatory, setTemporaryEditableSignatory] = useState<
    | Pick<
        Signatory,
        "key" | "id" | "firstname" | "lastname" | "email" | "phone"
      >
    | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [editMode, setEditMode] = useState<string>("");
  const [isReorderSignatoriesModalOpen, setIsReorderSignatoriesModalOpen] =
    useState<boolean>(false);

  const borderWarning =
    transaction.status === "draft" || transaction.status === "prepared";
  const isDraggable =
    transaction.status === "inProgress" &&
    authorized &&
    !editMode &&
    transaction.invitationMode === "sequential";
  // toSorted not working on some nodeJS version (chrome/firefox/edge)
  const nextSignatory = [...updatedSignatories]
    .sort(sortSignatories)
    .find((s) => s.status === "waiting" || s.status === "inProgress");
  const directSignature =
    nextSignatory &&
    nextSignatory.email === user?.email &&
    !!user.certificateInfos &&
    !!user.certificateInfos.serialNumber &&
    officeRights.includes("SIGNATURE_COLLECTE_SIGNER");
  const isOtherSignatoryInProgress =
    signatories &&
    user &&
    signatories.some(
      (signatory) =>
        signatory.status === "inProgress" && signatory.email !== user.email,
    );
  const nextSimultaneousSignatories = updatedSignatories.filter(
    (s) => s.status === "simultaneous",
  );

  useEffect(() => {
    if (transaction.status === "inProgress") {
      const fetchSignatoriesAreEditable = async () => {
        const updatedSignatories = await processSignatories(
          signatories,
          requestSignatoryIsEditable,
        );
        setUpdatedSignatories(updatedSignatories);
        setInitialSignatories(updatedSignatories);
      };

      void fetchSignatoriesAreEditable();
    }
  }, [signatories, transaction.status]);

  useEffect(() => {
    requestPutReorderSignatories.done.watch(({ result }) => {
      if (result.ok) {
        toastSuccess(t("success.transactionDetail.reorderSignatories"));
        if (transactionId) {
          void requestGetTransactionDetail({
            id: transactionId,
          });
        }
      } else {
        toastError(t("error.transactionDetail.reorderSignatories"));
      }
    });
  }, []);

  async function processSignatories(
    signatories: Signatory[],
    requestSignatoryIsEditable: any,
  ): Promise<Signatory[]> {
    const updatedSignatories = await Promise.all(
      signatories.map(async (signatory) => {
        try {
          const isEditable = await requestSignatoryIsEditable({
            id: signatory?.id ?? "",
          });
          return {
            ...signatory,
            key: signatory.id ?? signatory.key,
            isEditable: isEditable.data,
          };
        } catch (error) {
          console.error(
            "Erreur lors de la récupération de la valeur 'isEditable' du signataire:",
            signatory.id,
            error,
          );
          return {
            ...signatory,
            key: signatory.id ?? signatory.key,
            isEditable: false,
          };
        }
      }),
    );

    return updatedSignatories;
  }

  const isEmailTaken = () => {
    return signatories.find((signatory) => {
      return (
        signatory.email === temporaryEditableSignatory?.email &&
        signatory.id !== temporaryEditableSignatory?.id
      );
    });
  };

  const isSignatoryInProgress = (signatory: Signatory) =>
    transaction &&
    transaction.status === "inProgress" &&
    transaction.invitationMode === "sequential" &&
    (signatory.status === "waiting" ||
      signatory.status === "inProgress" ||
      signatory.status === "error" ||
      signatory.status === "simultaneous") &&
    authorized;

  const isLocked = (record: Signatory): boolean => record.status === "signed";

  const rowClassName = (signatory: Signatory) =>
    isSignatoryInProgress(signatory)
      ? "signatory-active"
      : "" + (isLocked(signatory) && "locked-row");

  const isOrderChanged = signatories
    .map((signatory) => signatory.id ?? signatory.key)
    .some(
      (id, i) =>
        id !== (updatedSignatories[i]?.id ?? updatedSignatories[i]?.key),
    );

  const reorderSignatories = () => {
    transaction.signatories = updatedSignatories;
    void requestPutReorderSignatories({
      id: transactionId,
      dto: transaction,
    });
  };

  const updateSignatory = () => {
    // If we are in 'inProgress' call server to update signatory
    if (transaction.status === "inProgress") {
      setIsLoading(true);
      if (
        !temporaryEditableSignatory?.email ||
        !RegexHelper.checkEmail(temporaryEditableSignatory?.email)
      ) {
        toastError(t("error.toast.format.email"));
        setIsLoading(false);
        return;
      }
      if (isEmailTaken()) {
        toastError(t("error.toast.format.emailTaken"));
        setIsLoading(false);
        return;
      }
      if (
        !temporaryEditableSignatory?.phone ||
        !RegexHelper.checkPhone(temporaryEditableSignatory?.phone)
      ) {
        toastError(t("error.toast.format.phone"));
        setIsLoading(false);
        return;
      }

      if (temporaryEditableSignatory) {
        requestUpdateSignatory({
          id: temporaryEditableSignatory.id,
          dto: temporaryEditableSignatory,
        })
          .then((response) => {
            if (response.ok && response.data) {
              setUpdatedSignatories(
                updatedSignatories.map((updatedSignatory) => {
                  if (
                    response.data &&
                    updatedSignatory.id === response.data.id
                  ) {
                    return { ...response.data, isEditable: true } as Signatory;
                  } else {
                    return updatedSignatory;
                  }
                }),
              );
            } else {
              toastError(
                t("error.toast.signatory", {
                  email: temporaryEditableSignatory.email,
                }),
              );
            }
          })
          .catch(() => {
            toastError(
              t("error.toast.signatory", {
                email: temporaryEditableSignatory.email,
              }),
            );
          })
          .finally(() => setIsLoading(false));
      }
    }
  };

  const handleValidateReorderSignatories = () => {
    const hasWaitingForSignatureStatus = signatories
      .filter((s) => s.status === "waiting" || s.status === "inProgress")
      .some((s) => {
        const oldIndex = signatories.findIndex(
          (sig) => (sig.id ?? sig.key) === (s.id ?? s.key),
        );
        const newIndex = updatedSignatories.findIndex(
          (sig) => (sig.id ?? sig.key) === (s.id ?? s.key),
        );
        return oldIndex !== newIndex;
      });
    if (hasWaitingForSignatureStatus) {
      setIsReorderSignatoriesModalOpen(true);
    } else {
      reorderSignatories();
    }
  };

  const handleOkReorderSignatoriesModal = () => {
    reorderSignatories();
    setIsReorderSignatoriesModalOpen(false);
  };

  return (
    <div className={`app-section-transaction-signatories ${className}`}>
      <div className="p-24 d-flex align-items-center justify-content-end flex-wrap gap-16 mb-8">
        <div className="flex-grow-1 d-flex align-items-center flex-wrap gap-8">
          <h2
            className="d-flex align-items-center text-black mr-8 mb-0"
            data-test="input-label"
            data-alt={t("transaction.detail.signatories")}
            data-test-for="invitationMode"
          >
            <ChevronIcon className="rotate-270 text-primary icon-30" />
            {t("transaction.detail.signatories")}
          </h2>
          <div
            className={`label-secondary pl-48 pr-48 border-${
              borderWarning ? "warning" : "primary"
            }`}
            data-test="invitationMode"
          >
            {transaction.invitationMode === "parallel"
              ? t("signature.invitationMode.parallel")
              : t("signature.invitationMode.sequential")}
          </div>
          <InfoModal
            value={
              transaction.invitationMode === "parallel"
                ? "invitationModeParallel"
                : "invitationModeSequential"
            }
            info
          />
        </div>
        {transaction.status === "inProgress" && (
          <ReloadIcon
            onClick={() => {
              if (transactionId) {
                void requestGetTransactionDetail({ id: transactionId });
              }
            }}
            className="text-tertiary cursor-pointer icon-30"
          />
        )}
      </div>
      <div className="px-24 pb-24">
        {transaction.cause &&
          transaction.cause === CANCELED_CAUSE.REVOCATION && (
            <div className="d-flex justify-content-center mb-16">
              {t("transaction.detail.canceledTransactionCauseRevocation")}
              <InfoModal
                value="canceledTransactionRevocation"
                info
                className="ml-4"
              />
            </div>
          )}
        {updatedSignatories && updatedSignatories.length > 0 ? (
          <div data-test={t("transaction.detail.signatories")}>
            <SortableTable
              isDraggable={isDraggable}
              data={updatedSignatories}
              setData={setUpdatedSignatories}
              columns={columns({
                isDraggable,
                transaction,
                documents,
                authorized,
                editMode,
                setEditMode,
                activateSignLevel2: transactionSettings?.activateSignLevel2,
                updateSignatory,
                isLoading,
                temporaryEditableSignatory,
                setTemporaryEditableSignatory,
                directSignature,
                isOrderChanged,
                signatureDegradedMode,
                isOtherSignatoryInProgress,
                setIsLoading,
                user,
                officeRights,
                hasMultipleNextSimultaneousSignatories:
                  nextSimultaneousSignatories.length > 1,
              })}
              className="min-height-table mb-16"
              rowClassName={rowClassName}
              isLocked={isLocked}
            />
          </div>
        ) : (
          <div className="mb-16" />
        )}
        {isOrderChanged && (
          <div className="d-flex justify-content-center gap-16">
            <BasicButton
              variant="secondary"
              className="px-64 py-8"
              text={t("buttons.cancel")}
              onClick={() => setUpdatedSignatories(initialSignatories)}
            />
            <BasicButton
              variant="primary"
              className="px-64 py-8"
              text={t("buttons.validate")}
              onClick={handleValidateReorderSignatories}
            />
          </div>
        )}
      </div>
      <ConfirmModal
        visible={isReorderSignatoriesModalOpen}
        onCancel={() => setIsReorderSignatoriesModalOpen(false)}
        onOk={handleOkReorderSignatoriesModal}
        title={t("transaction.detail.reorderSignatoriesModal.title")}
        content={t("transaction.detail.reorderSignatoriesModal.body")}
      />
    </div>
  );
};

export default TransactionSignatoriesSection;
